import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Search, X, FlaskConical } from 'lucide-react';
import { DataSources } from './DataSources';

const SECTION_IDS = ['welcome', 'capture', 'explore', 'proactive', 'sources', 'privacy', 'about'] as const;
type SectionId = (typeof SECTION_IDS)[number];

/**
 * Aide intégrée : sommaire à gauche, contenu à droite, recherche interne. Le
 * contenu est traduit (FR/DE) ; la section « sources » montre l'inventaire live.
 */
export function Help({ onClose, onShowDisclaimer }: { onClose: () => void; onShowDisclaimer: () => void }) {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [active, setActive] = useState<SectionId>('welcome');

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return [...SECTION_IDS];
    return SECTION_IDS.filter((id) =>
      `${t(`help.sections.${id}.title`)} ${t(`help.sections.${id}.body`)}`.toLowerCase().includes(needle),
    );
  }, [q, t]);

  const current: SectionId | undefined = filtered.includes(active) ? active : filtered[0];

  return (
    <div
      className="fixed inset-0 z-30 grid place-items-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t('help.title')}
      onClick={onClose}
    >
      <div
        className="flex w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-surface shadow-xl ring-1 ring-border"
        onClick={(e) => e.stopPropagation()}
      >
        {/* En-tête : titre + recherche + fermer */}
        <div className="flex items-center gap-3 border-b border-border px-4 py-3">
          <h2 className="text-base font-semibold">{t('help.title')}</h2>
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('help.searchPlaceholder')}
              className="w-full rounded-lg bg-surface-2 py-1.5 pl-8 pr-3 text-sm outline-none ring-1 ring-transparent focus:ring-accent"
            />
          </div>
          <button
            onClick={onClose}
            aria-label={t('help.close')}
            title={t('help.close')}
            className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-muted hover:bg-surface-2 hover:text-fg"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex max-h-[65vh] min-h-[16rem]">
          {/* Sommaire */}
          <nav className="w-44 shrink-0 overflow-y-auto border-r border-border p-2 sm:w-52">
            {filtered.length === 0 ? (
              <p className="px-2 py-3 text-xs text-muted">{t('help.noResult')}</p>
            ) : (
              <ul className="space-y-0.5">
                {filtered.map((id) => (
                  <li key={id}>
                    <button
                      onClick={() => setActive(id)}
                      aria-current={current === id}
                      className={`w-full rounded-lg px-2.5 py-1.5 text-left text-sm transition ${
                        current === id ? 'bg-accent/15 font-medium text-accent' : 'text-fg/80 hover:bg-surface-2'
                      }`}
                    >
                      {t(`help.sections.${id}.title`)}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </nav>

          {/* Contenu */}
          <div className="min-w-0 flex-1 overflow-y-auto px-5 py-4">
            {current && (
              <>
                <h3 className="mb-2 text-lg font-semibold">{t(`help.sections.${current}.title`)}</h3>
                <div className="md text-sm leading-relaxed">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{t(`help.sections.${current}.body`)}</ReactMarkdown>
                </div>
                {current === 'sources' && (
                  <div className="mt-3">
                    <DataSources />
                  </div>
                )}
                {current === 'welcome' && (
                  <button
                    onClick={onShowDisclaimer}
                    className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-surface-2 px-3 py-1.5 text-sm text-fg hover:bg-accent hover:text-accent-fg"
                  >
                    <FlaskConical className="h-4 w-4" /> {t('disclaimer.reopen')}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
